<template>
  <b-modal
    ref="upsertModal"
    v-model="showModal"
    centered
    size="md"
    :no-close-on-backdrop="true"
    cancel-variant="danger"
    :title="isUpdatingRole ? $t('edit_region') : $t('add_region')"
    @hidden="clearForm"
  >
    <b-overlay
      variant="transparent"
      opacity="0.6"
      :show="isFetching"
    >
      <ValidationObserver ref="vObserver">
        <b-row>
          <!--    NAME[UZ]    -->
          <b-col cols="12">
            <label> {{ $t('title') }} [UZ] </label>
            <ValidationProvider
              v-slot="{ errors }"
              name="name(uz)"
              rules="required"
            >
              <b-form-group>
                <b-form-input
                  v-model="details.name.uz"
                  :placeholder="`${$t('enter')} ${$t('title')} [UZ]`"
                  :state="errors.length > 0 ? false : null"
                  size="lg"
                />
              </b-form-group>
              <span class="validation__red">
                {{ errors[0] }}
              </span>
            </ValidationProvider>
          </b-col>

          <!--    NAME[RU]    -->
          <b-col cols="12">
            <label> {{ $t('title') }} [RU] </label>
            <ValidationProvider
              v-slot="{ errors }"
              name="name(ru)"
              rules="required"
            >
              <b-form-group>
                <b-form-input
                  v-model="details.name.ru"
                  :placeholder="`${$t('enter')} ${$t('title')} [RU]`"
                  :state="errors.length > 0 ? false : null"
                  size="lg"
                />
              </b-form-group>
              <span class="validation__red">{{ errors[0] }}</span>
            </ValidationProvider>
          </b-col>

          <b-col cols="12">
            <label> {{ $t('position') }} </label>
            <ValidationProvider
              v-slot="{ errors }"
              name="Position"
              rules="required"
            >
              <b-form-group>
                <b-form-input
                  v-model.number="details.position"
                  type="number"
                  :placeholder="$t('enter_position')"
                  :state="errors.length > 0 ? false : null"
                  size="lg"
                />
              </b-form-group>
              <span class="validation__red">{{ errors[0] }}</span>
            </ValidationProvider>
          </b-col>

          <b-col
            cols="12"
            class="my-1"
          >
            <b-form-checkbox v-model="details.public">
              {{ $t('is_published') }}
            </b-form-checkbox>
          </b-col>
        </b-row>

      </ValidationObserver>
    </b-overlay>

    <template #modal-footer>
      <b-overlay
        :show="isLoading"
        rounded
        opacity="0.6"
        spinner-small
        spinner-variant="primary"
        class="d-inline-block"
      >
        <b-button
          variant="primary"
          @click="submit"
        >
          {{ isUpdatingRole ? $t('edit') : $t('add') }}
        </b-button>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import { computed, ref, watchEffect } from '@vue/composition-api'
import {
  BModal,
  BButton,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BOverlay, BFormCheckbox,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'

import { isNull } from '@/util/inspect'
import useRegion from '@/composables/useRegion'
import useLoading from '@/composables/useLoading'

export default {
  components: {
    BFormCheckbox,
    BRow,
    BCol,
    BModal,
    BButton,
    BFormGroup,
    BFormInput,
    BOverlay,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    id: {
      type: [Number, String, Object],
      default: null,
    },
    methodType: {
      type: String,
      default: 'create',
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:show', 'update:data'],
  setup(props, { emit }) {
    const { create, edit, findOne } = useRegion()
    const {
      isLoading: isFetching,
      startLoading: startFetching,
      finishLoading: finishFetching,
    } = useLoading()

    const {
      isLoading,
      startLoading,
      finishLoading,
    } = useLoading()
    const vObserver = ref(null)
    const details = ref({
      name: {
        uz: '',
        ru: '',
      },
      public: true,
      position: null,
    })

    const showModal = computed({
      get() {
        return props.show
      },
      set(v) {
        emit('update:show', v)
      },
    })

    watchEffect(async () => {
      if (!isNull(props.id) && showModal.value) {
        try {
          startFetching()
          const response = await findOne({ id: props.id })
          details.value.name.uz = response.data.document.name.uz
          details.value.name.ru = response.data.document.name.ru
          details.value.position = response.data.document.position
        } finally {
          finishFetching()
        }
      }
    })

    function clearForm() {
      details.value.name.uz = ''
      details.value.name.ru = ''
      details.value.position = null
    }

    const isUpdatingRole = computed(() => props.methodType === 'update')

    async function submit() {
      const isValid = await vObserver.value.validate()

      if (!isValid) {
        return
      }

      try {
        startLoading()
        if (isUpdatingRole.value) {
          await edit({
            id: props.id,
            body: details.value,
          })
        } else {
          await create({
            body: details.value,
          })
        }

        showModal.value = false
        emit('update:data')
        clearForm()
        vObserver.value.reset()
      } finally {
        finishLoading()
      }
    }

    return {
      vObserver,
      isLoading,
      details,
      isUpdatingRole,
      showModal,
      isFetching,
      clearForm,
      submit,
    }
  },
}
</script>
